import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { IItinerary, useConfig } from "@thekilroygroup/shared-ui";
import {
  ITravellerAmount,
  cabinClassMap,
  createLegsData,
  findTravellerAmount,
  skyscannerPageTypeMap,
  skyscannerTripTypeMap,
} from "./metaUtils";
import { useLocation } from "react-router-dom";
import { getCurrency } from "utils/numberUtils";

/**
 * Example of a URL to fire a tracking event:
 * http://localhost:8080/search-results?fareId=CPHOSL2402161410D8D83228QAFALSE&utm_source=skyscannerDK&utm_campaign=skyscannerDK&utm_medium=cpa&hmac=oGe71WoBEa3%2FNLaaqLAWL9bJg8IVlPOfPttSo18KAxQ%3D&totalPrice=454&adultPrice=454&childPrice=0&infantPrice=0&clickId=3uVhS745T0e-gssykNwVAQ&timestampInSeconds=1705328601&adults=1&children=1&infants=1&cabinClass=ECONOMY&tripType=RETURN&outFrom=SAN&outTo=CPH&outDate=11.09.2024&retDate=25.09.2024
 */

interface IProps {
  selectedItinerary: IItinerary | null;
  eventType: "pre-conversion" | "conversion";
  createdDate: string;
  paymentMethod?: string;
  conversion_id?: string;
  price: number;
  totalPaidAmount?: number;
  fee?: number;
  marketingCarrier: string;
  fareBasis: string;
  skyscannerTrackingSourceId: string;
  skyscannerRedirectid: string;
}

export const SkyscannerTracking = (props: IProps) => {
  const {
    appConfig: { ENVIRONMENT },
  } = useConfig() as { appConfig: { ENVIRONMENT: string } };
  const [queryString, setQueryString] = useState<string | null>(null);
  const {
    selectedItinerary,
    eventType,
    createdDate,
    conversion_id,
    paymentMethod,
    totalPaidAmount,
    fee,
    marketingCarrier,
    fareBasis,
    skyscannerTrackingSourceId,
    skyscannerRedirectid,
    price,
  } = props;
  interface SkyscannerEventPreConversion {
    [key: string]: any;
  }

  interface SkyscannerConversionEvent {
    [key: string]: any;
  }

  const objectToQueryString = (
    obj: SkyscannerConversionEvent | SkyscannerEventPreConversion,
  ) => {
    return Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");
  };

  const page = useLocation().pathname;

  const createTrackingObject = () => {
    if (eventType === "pre-conversion") {
      const preConversionObject: SkyscannerEventPreConversion = {
        skyscanner_redirectid: skyscannerRedirectid,
        tracking_source_id: skyscannerTrackingSourceId,
        event_type: eventType,
        occurred_at: createdDate,
        price: price,
        currency: selectedItinerary ? getCurrency(selectedItinerary) : "_",
        stage:
          skyscannerPageTypeMap[page as keyof typeof skyscannerPageTypeMap], // Add type assertion to keyof typeof pageTypeMap
        fare_basis_code: fareBasis,
        marketing_carrier: marketingCarrier,
        platform: "web",
        // Device type might be handy to track, but it's not implemented in the current version of the tracking pixel
        // device: getUserDevice(), // The device type that the user is using
      };

      setQueryString(objectToQueryString(preConversionObject));
    } else if (
      eventType === "conversion" &&
      paymentMethod &&
      totalPaidAmount &&
      selectedItinerary
    ) {
      const travellers: ITravellerAmount =
        findTravellerAmount(selectedItinerary);

      const conversionObject: SkyscannerConversionEvent = {
        skyscanner_redirectid: skyscannerRedirectid,
        tracking_source_id: skyscannerTrackingSourceId,
        event_type: eventType,
        occurred_at: createdDate,
        conversion_id: conversion_id, // string, // Your unique identifier for the transaction/order
        transaction_total: String(totalPaidAmount), //string, // The total cost of transaction, including all fees
        flight_fare: selectedItinerary?.totalFareAmounts.total || 0, // number, // The total cost of the flight(s) in the booking including taxes and surcharges, excluding additional ancillaries, products, payment fees etc.
        payment_fee: String(fee), //string, // The total payment fee the traveller has/will pay (e.g. payment method fee)
        currency: selectedItinerary ? getCurrency(selectedItinerary) : "_", // Transaction's currency, using ISO-4217 code
        payment_method: paymentMethod, //string, // Payment method that users choose to pay with
        fare_basis_code: fareBasis, // A fare basis code is an alphabetic or alpha-numeric code used by airlines to identify a fare type and allow airline staff and travel agents to find the rules applicable to that fare
        marketing_carrier: marketingCarrier, // Marketing carrier IATA code
        legs: createLegsData(selectedItinerary), // string, // Flight leg information in custom format
        cabin_class: selectedItinerary
          ? cabinClassMap[selectedItinerary.cabinClass]
          : "",
        route_type: selectedItinerary
          ? skyscannerTripTypeMap[selectedItinerary.tripType]
          : "", //"one-way" | "return" | "multiple-destination"; // Route type
        haul_type: "long-haul", //"domestic" | "long-haul" | "short-haul"; // The partner's own classification of the itinerary
        platform: "web", // The partner’s platform that traveller is on when each event is triggered
        adult_number: travellers.adults, // Number of adults
        child_number: travellers.children, // Number of children
        infant_number: travellers.infants, // Number of infants
        // Device type might be handy to track, but it's not implemented in the current version of the tracking pixel
        // device: getUserDevice(), // The device type that the user is using
      };

      setQueryString(objectToQueryString(conversionObject));
    }
  };

  useEffect(() => {
    createTrackingObject();
  }, [selectedItinerary]);

  // const logTracking = () => {
  //   console.log(
  //     `%cMetapartner tracking is disabled in dev environments\n%cIn production, tracking is fired with the following query string:\n\n%c${queryString}`,
  //     "color: purple; font-size: 18px;",
  //     "color: black; font-size: 12px;",
  //     "color: #FF3C00; font-size: 16px;"
  //   );
  //   return "";
  // };
  return (
    <Box>
      {/*
       * Only log tracking in dev environments
       */}
      {/*queryString && ENVIRONMENT.toLowerCase() === "prod" && (
        <img
          src={`https://analytics.skyscanner.net/dv/v1/event.png?${queryString}`}
          style={{ width: "1px", height: "1px", borderStyle: "none" }}
        />
      )*/}
      {/*queryString &&
        ENVIRONMENT.toLocaleLowerCase() !== "prod" &&
        logTracking()*/}
      {/*
       * Always log tracking in all environments
       */}
      {queryString && (
        <img
          src={`https://analytics.skyscanner.net/dv/v1/event.png?${queryString}`}
          style={{ width: "1px", height: "1px", borderStyle: "none" }}
        />
      )}
    </Box>
  );
};
